import React, { useState, useMemo } from "react";
import { Tabs, Table, Tag, Empty, Spin, Collapse } from "antd";
import type { ColumnsType, TabsProps } from "antd";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useGetBookings } from "~/services/booking";
import { TBooking } from "~/types/booking";
import { JobStatus } from "~/utils/constants";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);

// Helper to calculate dynamic booking status based on service tickets
const calculateDynamicStatus = (booking: TBooking): JobStatus => {
  const serviceTickets = booking.serviceTicket || [];

  // If no service tickets, it's a DRAFT
  if (serviceTickets.length === 0) {
    return JobStatus.DRAFT;
  }

  let completedCount = 0;
  let inProgressCount = 0;

  serviceTickets.forEach((ticket: any) => {
    if (ticket.timeOut || ticket.completedAt || ticket.completed_at) {
      completedCount++;
    } else if (ticket.timeIn) {
      inProgressCount++;
    }
  });

  if (completedCount === serviceTickets.length && serviceTickets.length > 0) {
    return JobStatus.COMPLETED;
  }
  if (completedCount > 0 || inProgressCount > 0) {
    return JobStatus.IN_PROGRESS;
  }

  // Use the stored status field for ESTIMATED vs SCHEDULED
  return booking.status || JobStatus.SCHEDULED;
};

export const JobStatusOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState<JobStatus>(JobStatus.SCHEDULED);

  // Fetch all recent bookings and filter client-side
  const { data: allBookingsData, isLoading } = useGetBookings({
    page: 1,
    pageSize: 200, // Fetch more to ensure we get all active bookings
  });

  // Filter bookings by calculated status
  const filteredBookings = useMemo(() => {
    if (!allBookingsData?.items) {
      return { draft: [], estimated: [], scheduled: [], inProgress: [], completed: [] };
    }

    const draft: TBooking[] = [];
    const estimated: TBooking[] = [];
    const scheduled: TBooking[] = [];
    const inProgress: TBooking[] = [];
    const completed: TBooking[] = [];

    // Filter completed bookings to last 10 days (today + previous 9 days)
    const tenDaysAgo = dayjs().subtract(9, "days").startOf('day');

    allBookingsData.items.forEach((booking) => {
      const dynamicStatus = calculateDynamicStatus(booking);

      switch (dynamicStatus) {
        case JobStatus.DRAFT:
          draft.push(booking);
          break;
        case JobStatus.ESTIMATED:
          estimated.push(booking);
          break;
        case JobStatus.SCHEDULED:
          scheduled.push(booking);
          break;
        case JobStatus.IN_PROGRESS:
          inProgress.push(booking);
          break;
        case JobStatus.COMPLETED:
          // Only include completed bookings from last 10 days
          // Find the most recent completion date from service tickets
          let mostRecentCompletionDate: Date | null = null;

          booking.serviceTicket?.forEach((ticket: any) => {
            const ticketCompletionDate = ticket.timeOut || ticket.time_out || ticket.completedAt || ticket.completed_at;
            if (ticketCompletionDate) {
              const completionDate = new Date(ticketCompletionDate);
              if (!mostRecentCompletionDate || completionDate > mostRecentCompletionDate) {
                mostRecentCompletionDate = completionDate;
              }
            }
          });

          // Include if any service ticket was completed in the last 10 days
          if (mostRecentCompletionDate && dayjs(mostRecentCompletionDate).isSameOrAfter(tenDaysAgo, 'day')) {
            completed.push(booking);
          }
          break;
      }
    });

    return { draft, estimated, scheduled, inProgress, completed };
  }, [allBookingsData]);

  // Get status badge color
  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.DRAFT:
        return "default";
      case JobStatus.ESTIMATED:
        return "blue";
      case JobStatus.SCHEDULED:
        return "orange";
      case JobStatus.IN_PROGRESS:
        return "processing";
      case JobStatus.COMPLETED:
        return "success";
      case JobStatus.CANCELLED:
        return "error";
      default:
        return "default";
    }
  };

  // Calculate total inventory count across all service tickets using the count field
  const getInventoryCount = (booking: TBooking) => {
    if (!booking.serviceTicket) return 0;
    return booking.serviceTicket.reduce((total, ticket: any) => {
      // Use the inventoryCount field loaded by TypeORM
      const count = ticket.inventoryCount || 0;
      return total + parseInt(count, 10);
    }, 0);
  };

  // Define table columns
  const columns: ColumnsType<TBooking> = [
    {
      title: "Reference #",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 140,
      render: (text: string, record: TBooking) => (
        <Link
          to={`/bookings/${record.id}`}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          #{text}
        </Link>
      ),
    },
    {
      title: "Client",
      key: "client",
      width: 200,
      render: (_: any, record: TBooking) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {record.client?.companyName ||
              `${record.client?.firstName || ""} ${record.client?.lastName || ""}`.trim() ||
              "N/A"}
          </span>
          {record.project?.name && (
            <span className="text-xs text-gray-500">{record.project.name}</span>
          )}
        </div>
      ),
    },
    {
      title: "Services",
      key: "services",
      width: 100,
      align: "center",
      render: (_: any, record: TBooking) => (
        <Tag color="blue">{record.serviceTicket?.length || 0}</Tag>
      ),
    },
    {
      title: "Inventory",
      key: "inventory",
      width: 100,
      align: "center",
      render: (_: any, record: TBooking) => (
        <Tag color="purple">{getInventoryCount(record)}</Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_: any, record: TBooking) => {
        const dynamicStatus = calculateDynamicStatus(record);
        return (
          <Tag color={getStatusColor(dynamicStatus)}>
            {dynamicStatus.replace(/-/g, " ")}
          </Tag>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: Date) => (
        <span className="text-sm text-gray-600">
          {dayjs(date).format("MMM D, YYYY")}
        </span>
      ),
    },
    {
      title: "Recent Actions",
      key: "recentActions",
      width: 180,
      render: (_: any, record: TBooking) => {
        // Since updatedAt is not returned by API, use service ticket dates and createdAt
        const activities: Array<{ date: Date; action: string }> = [];

        // Check service tickets for recent changes
        if (record.serviceTicket && record.serviceTicket.length > 0) {
          record.serviceTicket.forEach((ticket) => {
            // Try different field name variations
            const ticketUpdated = ticket.updated_at || ticket.updatedAt;
            const ticketCreated = ticket.created_at || ticket.createdAt;

            if (ticketUpdated) {
              const updatedDate = new Date(ticketUpdated);
              const createdDate = ticketCreated ? new Date(ticketCreated) : updatedDate;

              // Check if service was recently created (within 5 minutes of updated time)
              const isNewService = Math.abs(updatedDate.getTime() - createdDate.getTime()) < 300000;

              activities.push({
                date: updatedDate,
                action: isNewService ? "Service created" : "Service updated",
              });
            } else if (ticketCreated) {
              // If no updated date, use created date
              activities.push({
                date: new Date(ticketCreated),
                action: "Service created",
              });
            }
          });
        }

        // Fallback to booking creation date
        if (record.createdAt) {
          activities.push({
            date: new Date(record.createdAt),
            action: "Booking created",
          });
        }

        // Get the most recent activity
        if (activities.length === 0) {
          return (
            <span className="text-sm text-gray-400 italic">No data</span>
          );
        }

        const mostRecent = activities.reduce((latest, current) =>
          current.date > latest.date ? current : latest
        );

        return (
          <div className="text-sm">
            <div className="text-gray-900 font-medium">{mostRecent.action}</div>
            <div className="text-gray-500 text-xs">
              {dayjs(mostRecent.date).fromNow()}
            </div>
          </div>
        );
      },
    },
  ];

  // Get data based on active tab
  const currentBookings = useMemo(() => {
    switch (activeTab) {
      case JobStatus.DRAFT:
        return filteredBookings.draft;
      case JobStatus.SCHEDULED:
        return filteredBookings.scheduled;
      case JobStatus.IN_PROGRESS:
        return filteredBookings.inProgress;
      case JobStatus.ESTIMATED:
        return filteredBookings.estimated;
      case JobStatus.COMPLETED:
        return filteredBookings.completed;
      default:
        return filteredBookings.scheduled;
    }
  }, [activeTab, filteredBookings]);

  const items: TabsProps["items"] = [
    {
      key: JobStatus.DRAFT,
      label: (
        <div className="flex items-center space-x-2">
          <span>Draft</span>
          <Tag color="default">{filteredBookings.draft.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.ESTIMATED,
      label: (
        <div className="flex items-center space-x-2">
          <span>Estimated</span>
          <Tag color="blue">{filteredBookings.estimated.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.SCHEDULED,
      label: (
        <div className="flex items-center space-x-2">
          <span>Scheduled</span>
          <Tag color="orange">{filteredBookings.scheduled.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.IN_PROGRESS,
      label: (
        <div className="flex items-center space-x-2">
          <span>In Progress</span>
          <Tag color="processing">{filteredBookings.inProgress.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.COMPLETED,
      label: (
        <div className="flex items-center space-x-2">
          <span>Completed (10d)</span>
          <Tag color="success">{filteredBookings.completed.length}</Tag>
        </div>
      ),
    },
  ];

  const collapseItems = [
    {
      key: "1",
      label: (
        <div className="flex items-center justify-between w-full pr-4">
          <div>
            <h2 className="text-lg font-semibold">Booking Matrix</h2>
            <p className="text-sm text-gray-500 mt-1">
              Track your bookings by status
            </p>
          </div>
          <div className="flex items-center gap-4">
            {filteredBookings.draft.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Draft:</span>
                <Tag color="default">{filteredBookings.draft.length}</Tag>
              </div>
            )}
            {filteredBookings.estimated.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Estimated:</span>
                <Tag color="blue">{filteredBookings.estimated.length}</Tag>
              </div>
            )}
            {filteredBookings.scheduled.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Scheduled:</span>
                <Tag color="orange">{filteredBookings.scheduled.length}</Tag>
              </div>
            )}
            {filteredBookings.inProgress.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">In Progress:</span>
                <Tag color="processing">{filteredBookings.inProgress.length}</Tag>
              </div>
            )}
            {filteredBookings.completed.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Completed (10d):</span>
                <Tag color="success">{filteredBookings.completed.length}</Tag>
              </div>
            )}
          </div>
        </div>
      ),
      children: (
        <div>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as JobStatus)}
            items={items}
            className="-mt-4"
          />
          <div className="pb-6">
            <Spin spinning={isLoading}>
              {currentBookings.length === 0 ? (
                <Empty
                  description={`No ${activeTab.toLowerCase().replace(/-/g, " ")} bookings`}
                  className="py-8"
                />
              ) : (
                <Table
                  columns={columns}
                  dataSource={currentBookings}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  className="border rounded"
                />
              )}
            </Spin>
            {currentBookings.length > 0 && (
              <div className="mt-4 text-right">
                <Link
                  to={`/bookings?status=${activeTab}`}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  View all {activeTab.toLowerCase().replace(/-/g, " ")} bookings →
                </Link>
              </div>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg border">
      <Collapse
        items={collapseItems}
        defaultActiveKey={[]}
        expandIcon={({ isActive }) => (
          <ChevronDown
            size={20}
            className={`transition-transform ${isActive ? "rotate-180" : ""}`}
          />
        )}
        className="bg-transparent border-none"
      />
    </div>
  );
};
